import React, { useState } from 'react';
import { User } from "lucide-react";
import { AccountOverview } from "@/components/account/AccountOverview";
import { BillingHistory } from "@/components/account/BillingHistory";
import { AccountStats } from "@/components/account/AccountStats";
import { AccountActivity } from "@/components/account/AccountActivity";
import { AccountNotifications } from "@/components/account/AccountNotifications";
import { AccountTeamManagement } from "@/components/account/TeamManagement";
import { useAuth } from "@/contexts/AuthContext";
import RBACTest from './RBACTest';

const tabs = [
  { id: "overview", label: "Overview" },
  { id: "billing", label: "Billing History" },
  // Hidden until v2/v3 — components + render blocks below remain in the repo;
  // re-enable by uncommenting these (and the Admin append below):
  // { id: "activity", label: "Recent Activity" },
  // { id: "stats", label: "Statistics" },
  // { id: "notifications", label: "Notifications" },
  // { id: "team", label: "Team Management" },
];

const Account = () => {
  const { user, userRoles } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");

  const hasAdminAccess = userRoles.some(role =>
    role.name === 'system_admin' || role.name === 'admin' || role.display_name?.toLowerCase().includes('admin')
  );
  const fallbackAdminAccess = user && userRoles.length === 0 && user.email === 'ivan@digitaldominator.com.au';
  const showAdminTab = hasAdminAccess || fallbackAdminAccess;

  // Admin tab hidden until v2/v3 — restore by switching this back to:
  // showAdminTab ? [...tabs, { id: "admin", label: "Admin" }] : tabs;
  const allTabs = tabs;

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] mx-auto px-6 py-6 md:px-10 lg:px-14" style={{ ["--accent" as string]: "#8B5CF6", maxWidth: "1320px" } as React.CSSProperties}>
        {/* Header */}
        <div className="mb-[22px] flex items-start gap-3.5">
          <div className="mt-0.5 grid h-9 w-9 flex-none place-items-center rounded-[10px]" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
            <User className="h-[19px] w-[19px]" style={{ color: "var(--accent)" }} />
          </div>
          <div>
            <h1 className="font-heading text-[30px] leading-none text-og01-text">Account</h1>
            <p className="mt-1 text-[13.5px] text-og01-text-secondary">Manage your account settings and preferences</p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-[22px] flex w-fit max-w-full items-center gap-1 overflow-x-auto rounded-[14px] border border-white/[0.07] bg-og01-card p-1.5">
          {allTabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="whitespace-nowrap rounded-[9px] px-4 py-2.5 text-[13.5px] font-medium transition-colors hover:text-og01-text"
                style={isActive
                  ? { color: "var(--accent)", background: "color-mix(in srgb, var(--accent) 16%, #0F0F18)", border: "1px solid color-mix(in srgb, var(--accent) 36%, transparent)" }
                  : { color: "#9AA0B3", border: "1px solid transparent" }}>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === "overview" && <AccountOverview />}
          {activeTab === "billing" && <BillingHistory />}
          {activeTab === "activity" && <AccountActivity />}
          {activeTab === "stats" && <AccountStats />}
          {activeTab === "notifications" && <AccountNotifications />}
          {activeTab === "team" && <AccountTeamManagement />}
          {activeTab === "admin" && showAdminTab && (
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-bold" style={{ color: "#E8E4FF" }}>Admin Panel</h2>
                <p className="text-sm mt-1" style={{ color: "#9AA0B3" }}>Administrative tools and role-based access control</p>
                {fallbackAdminAccess && (
                  <div className="mt-3 px-4 py-2 rounded-xl text-xs" style={{ background: "rgba(255,154,74,0.1)", border: "1px solid rgba(255,154,74,0.2)", color: "#FF9A4A" }}>
                    Showing admin access via fallback (roles not loaded from AuthContext)
                  </div>
                )}
              </div>
              <RBACTest />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Account;
